import React, { useRef, useEffect, useState, useCallback } from 'react';
import type { SkillDrumProps } from '../about.types';
import './SkillDrum.styles.css';

// * Cylinder curvature configuration constants
const RADIUS_PX = 180;
const STEP_DEG = 24;
const IDLE_ROTATION_MS = 2000;
const WHEEL_THRESHOLD_PX = 35;
const DESKTOP_OFFSETS = [-2, -1, 0, 1, 2] as const;
const MOBILE_OFFSETS = [-1, 0, 1] as const;

// * [COMPONENT] 3D Cylindrical Skill Drum revolving on X-axis with Depth-of-Field Blur
export const SkillDrum: React.FC<SkillDrumProps> = ({
  skills,
  activeIndex,
  onSelectSkill,
  isSectionActive = true,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 900 : false
  );
  const [isInteracting, setIsInteracting] = useState<boolean>(false);
  const accumulatedDeltaRef = useRef<number>(0);
  const touchStartYRef = useRef<number | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // * Responsive screen width listener for focal window count
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const visibleOffsets = isMobile ? MOBILE_OFFSETS : DESKTOP_OFFSETS;
  const total = skills.length;

  // * Navigate drum index cyclically
  const advanceIndex = useCallback(
    (delta: number) => {
      if (total === 0) return;
      const nextIndex = (activeIndex + delta + total * 100) % total;
      onSelectSkill(nextIndex);
    },
    [activeIndex, onSelectSkill, total]
  );

  // * Signal interaction pause and schedule auto-resume
  const registerInteraction = useCallback(() => {
    setIsInteracting(true);
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    resumeTimeoutRef.current = setTimeout(() => {
      setIsInteracting(false);
    }, 2800);
  }, []);

  // * Idle rotation ticker: 1 item per 2000ms when section is active and idle
  useEffect(() => {
    if (!isSectionActive || isInteracting || total <= 1) {
      if (idleTimerRef.current) {
        clearInterval(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    try {
      idleTimerRef.current = setInterval(() => {
        advanceIndex(1);
      }, IDLE_ROTATION_MS);
    } catch (err) {
      console.error('Failed to initialize idle drum rotation:', err);
    }

    return () => {
      if (idleTimerRef.current) {
        clearInterval(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [advanceIndex, isInteracting, isSectionActive, total]);

  // * Independent wheel event listener isolated from global GSAP Observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      try {
        e.stopPropagation();
        e.preventDefault();
        registerInteraction();

        accumulatedDeltaRef.current += e.deltaY;
        if (Math.abs(accumulatedDeltaRef.current) >= WHEEL_THRESHOLD_PX) {
          const step = accumulatedDeltaRef.current > 0 ? 1 : -1;
          accumulatedDeltaRef.current = 0;
          advanceIndex(step);
        }
      } catch (err) {
        console.error('Wheel scrubbing handler error:', err);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [advanceIndex, registerInteraction]);

  // * Touch scrubbing handlers for mobile / touchpads
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    registerInteraction();
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartYRef.current === null) return;
    try {
      const currentY = e.touches[0].clientY;
      const deltaY = touchStartYRef.current - currentY;
      if (Math.abs(deltaY) >= WHEEL_THRESHOLD_PX) {
        const step = deltaY > 0 ? 1 : -1;
        touchStartYRef.current = currentY;
        advanceIndex(step);
      }
    } catch (err) {
      console.error('Touch scrubbing handler error:', err);
    }
  };

  const handleTouchEnd = () => {
    touchStartYRef.current = null;
    registerInteraction();
  };

  // * Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`skill-drum-wrapper ${className}`}
      onMouseEnter={registerInteraction}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="region"
      aria-label="Interactive 3D Skill Drum"
    >
      <div className="skill-drum-viewport">
        {/* * Center Focal Bracket Indicator */}
        <div className="skill-drum-focal-bracket" aria-hidden="true" />

        {/* * Cylindrical Focal Stack (5 items desktop, 3 items mobile) */}
        {visibleOffsets.map((offset) => {
          const itemIndex = (activeIndex + offset + total * 100) % total;
          const skill = skills[itemIndex];
          if (!skill) return null;

          const absOffset = Math.abs(offset);
          const radius = isMobile ? 140 : RADIUS_PX;
          const stepDeg = isMobile ? 22 : STEP_DEG;
          const angleDeg = offset * stepDeg;
          const angleRad = (angleDeg * Math.PI) / 180;
          const translateY = Math.sin(angleRad) * radius;
          const translateZ = Math.cos(angleRad) * radius - radius;
          const rotateX = -angleDeg;

          // * Depth-of-Field Blur Gradient calculation
          let blurTierClass = 'blur-tier-50';
          if (absOffset === 0) {
            blurTierClass = 'blur-tier-0';
          } else if (absOffset === 1) {
            blurTierClass = 'blur-tier-25';
          }

          return (
            <button
              key={`${skill.id}-${offset}`}
              type="button"
              className={`skill-drum-item ${blurTierClass}`}
              style={{
                transform: `translate3d(0, ${translateY}px, ${translateZ}px) rotateX(${rotateX}deg)`,
              }}
              onClick={() => {
                registerInteraction();
                advanceIndex(offset);
              }}
              aria-label={`Select skill: ${skill.title} (${skill.experienceYears})`}
              aria-current={offset === 0 ? 'true' : 'false'}
            >
              <div className="skill-item-content">
                <span className="skill-item-title">{skill.title}</span>
                <span className="skill-item-category">{skill.category}</span>
              </div>
              <span className="skill-item-badge">{skill.experienceYears}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SkillDrum;
